import moment from "moment";
import { RRuleSet, rrulestr } from "rrule";
import { ScheduleItemDto, ScheduleItemUpdateDto } from "../dtos/scheduleItemDto";
import { ScheduleAction, ScheduleItem } from "@lib/types";
import { convertDateToUtc } from "../../../../../services/host/src/controls/schedule/utils/convertDateToUtc";

/** Schedule item mapper. */
class ScheduleItemMapper {

    private toDateDto(date: Date): string {
        return moment.utc(date).format("YYYYMMDDTHHmmss");
    }

    private fromDateDto(dto: string): Date {
        return moment(dto, "YYYYMMDDTHHmmss").toDate();
    }

    /**
     * Maps dto to model.
     * @param dto Dto.
     */
    public fromDto(dto: ScheduleItemDto): ScheduleItem {
        const rruleSet = rrulestr(dto.rrule, {
            forceset: true,
        }) as RRuleSet;

        dto.exdate.forEach(dateDto => {
            const date = this.fromDateDto(dateDto);
            rruleSet.exdate(convertDateToUtc(date));
        });

        return {
            id: dto.id,
            parentId: dto.parentId !== 0 ?
                dto.parentId :
                null,
            name: dto.name,
            priority: dto.priority,
            rrule: rruleSet,
            userSettings: {
                execute: dto.userSettings.execute !== "" ?
                    dto.userSettings.execute :
                    null,
            },
            enabled: dto.enabled,
            startDate: this.fromDateDto(dto.startDate),
            endDate: dto.endDate !== "" ?
                this.fromDateDto(dto.endDate) :
                null,
            action: {
                type: dto.action.type === "giveAnImpulse" ?
                    ScheduleAction.Impulse :
                    ScheduleAction.SetParameter,
                parameter: dto.action.parameter,
                parameterId: dto.action.parameterId,
                pulseDuration: dto.action.pulseDuration,
                value: dto.action.value,
            },
            comment: dto.comment,
            color: dto.color,
            writeToArchive: dto.writeToArchive,
            archiveMessage: dto.message,
        };
    }

    /**
     * Maps model to dto.
     * @param model Model.
     */
    public toDto(model: ScheduleItem): ScheduleItemDto {
        return {
            id: model.id,
            parentId: model.parentId ?? 0,
            name: model.name,
            priority: model.priority,
            rrule: model.rrule.rrules()[0].toString(),
            exdate: model.rrule.exdates().map(date => this.toDateDto(date)),
            userSettings: {
                execute: model.userSettings.execute !== null ?
                    model.userSettings.execute :
                    "",
            },
            enabled: model.enabled,
            startDate: this.toDateDto(model.rrule.rrules()[0].options.dtstart),
            endDate: model.endDate !== null ?
                this.toDateDto(convertDateToUtc(model.endDate)) :
                "",
            action: {
                type: model.action.type === ScheduleAction.Impulse ?
                    "giveAnImpulse" :
                    "setParameter",
                parameter: model.action.parameter,
                parameterId: model.action.parameterId,
                pulseDuration: model.action.pulseDuration,
                value: model.action.value,
            },
            comment: model.comment,
            color: model.color,
            writeToArchive: model.writeToArchive,
            message: model.archiveMessage,
        };
    }

    /**
     * Maps model to update dto.
     * @param model Model.
     */
    public toUpdateDto(model: ScheduleItem): ScheduleItemUpdateDto {
        return {
            taskId: model.id,
            parentId: model.parentId ?? 0,
            name: model.name,
            priority: model.priority,
            rrule: model.rrule.rrules()[0].toString(),
            exdate: model.rrule.exdates().map(date => this.toDateDto(date)),
            userSettings: {
                execute: model.userSettings.execute !== null ?
                    model.userSettings.execute :
                    "",
            },
            enabled: model.enabled,
            startDate: this.toDateDto(model.rrule.rrules()[0].options.dtstart),
            endDate: model.endDate !== null ?
                this.toDateDto(convertDateToUtc(model.endDate)) :
                "",
            action: {
                type: model.action.type === ScheduleAction.Impulse ?
                    "giveAnImpulse" :
                    "setParameter",
                parameter: model.action.parameter,
                parameterId: model.action.parameterId,
                pulseDuration: model.action.pulseDuration,
                value: model.action.value,
            },
            comment: model.comment,
            color: model.color,
            writeToArchive: model.writeToArchive,
            message: model.archiveMessage,
        };
    }
}

export const scheduleItemMapper = new ScheduleItemMapper();
